import { EditorState } from 'draft-js';
import React, { SetStateAction, useCallback } from 'react';
import { Editor } from 'react-draft-wysiwyg';

import { useDocumentApi } from '../../../api/document/useDocumentApi';

import { BASE_DOCUMENT_API_HOST } from '../../../utils/api.util';
import { toolbarConfig } from '../../../utils/text-editor.util';
import { getClassNames } from '../../../utils/util';

import { ReactComponent as CenterAlignActiveIcon } from '../../../resources/icons/center-align-active.svg';
import { ReactComponent as CenterAlignIcon } from '../../../resources/icons/center-align.svg';
import { ReactComponent as LeftAlignActiveIcon } from '../../../resources/icons/left-align-active.svg';
import { ReactComponent as LeftAlignIcon } from '../../../resources/icons/left-align.svg';
import { ReactComponent as RightAlignActiveIcon } from '../../../resources/icons/right-align-active.svg';
import { ReactComponent as RightAlignIcon } from '../../../resources/icons/right-align.svg';

import './rich-text-editor.scss';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

const IMAGE_ALIGNMENTS = [
  { value: 'left', Icon: LeftAlignIcon, ActiveIcon: LeftAlignActiveIcon },
  { value: 'center', Icon: CenterAlignIcon, ActiveIcon: CenterAlignActiveIcon },
  { value: 'right', Icon: RightAlignIcon, ActiveIcon: RightAlignActiveIcon }
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ImageBlock = ({ block, contentState, blockProps }: any) => {
  const entityKey = block.getEntityAt(0);
  const { src, alt, height, width, alignment } = contentState.getEntity(entityKey).getData();
  const { getEditorState, onChange, disabled } = blockProps;

  const displayAlignment = alignment || 'left';

  const setAlignment = (value: string) => {
    const currentContent = getEditorState().getCurrentContent();
    currentContent.mergeEntityData(entityKey, { alignment: value });
    onChange(EditorState.push(getEditorState(), currentContent, 'change-block-data'));
  };

  return (
    <div className={getClassNames('rich-text-editor__image-align', { [displayAlignment]: true })}>
      <div className="rich-text-editor__image">
        <img src={src} alt={alt} style={{ height: height || 'auto', width: width || 'auto' }} />
        {!disabled && (
          <div className="rich-text-editor__image-alignment">
            {IMAGE_ALIGNMENTS.map(({ value, Icon, ActiveIcon }) => {
              const AlignmentIcon = value === displayAlignment ? ActiveIcon : Icon;

              return (
                <button
                  key={value}
                  type="button"
                  className="rich-text-editor__image-alignment__option"
                  onClick={() => setAlignment(value)}
                >
                  <AlignmentIcon />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

type Props = {
  editorState?: object;
  setEditorState: React.Dispatch<SetStateAction<object | undefined>>;
  disabled?: boolean;
};

const RichTextEditor = ({ editorState, setEditorState, disabled = false }: Props) => {
  const { uploadDocument } = useDocumentApi();

  const customBlockRenderFunc = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (contentBlock: any, { getEditorState, onChange }: any) => {
      if (contentBlock.getType() !== 'atomic') return undefined;

      const entityKey = contentBlock.getEntityAt(0);
      if (!entityKey) return undefined;

      const entity = getEditorState().getCurrentContent().getEntity(entityKey);
      if (entity.getType() !== 'IMAGE') return undefined;

      return { component: ImageBlock, editable: false, props: { getEditorState, onChange, disabled } };
    },
    [disabled]
  );

  const uploadImageCallback = useCallback(
    (file: File) =>
      new Promise((resolve, reject) => {
        uploadDocument(file, (documentExternalReference) => {
          if (!documentExternalReference) {
            reject(new Error('Image upload failed'));
            return;
          }

          resolve({
            data: {
              link: `${BASE_DOCUMENT_API_HOST}/rest/service/document/retrieve/octet-stream?ref=${documentExternalReference}`
            }
          });
        });
      }),
    [uploadDocument]
  );

  return (
    <Editor
      editorState={editorState}
      onEditorStateChange={setEditorState}
      toolbar={{ ...toolbarConfig, image: { ...toolbarConfig.image, uploadCallback: uploadImageCallback } }}
      customBlockRenderFunc={customBlockRenderFunc}
      wrapperClassName="rich-text-editor__wrapper"
      editorClassName="rich-text-editor__editor"
      toolbarClassName="rich-text-editor__toolbar"
      readOnly={disabled}
    />
  );
};

export default RichTextEditor;
